import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PrisonProvider } from './context/PrisonContext';
import { Toaster } from 'react-hot-toast';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import MetaPixelTracker from './components/MetaPixelTracker';
import AppChoiceModal from './components/AppChoiceModal';
import './App.css';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const LetterCategoryPage = lazy(() => import('./pages/LetterCategoryPage'));
const NewLetterPage = lazy(() => import('./pages/NewLetterPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const DraftLettersPage = lazy(() => import('./pages/DraftLettersPage'));
const SentLettersPage = lazy(() => import('./pages/SentLettersPage'));
const LetterDetailPage = lazy(() => import('./pages/LetterDetailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const KvkkAydinlatmaPage = lazy(() => import('./pages/KvkkAydinlatmaPage'));
const DistanceSellingAgreementPage = lazy(() => import('./pages/DistanceSellingAgreementPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ReferralHandler = lazy(() => import('./pages/ReferralHandler'));
const InvitationEditorPage = lazy(() => import('./pages/InvitationEditorPage'));
const PublicInvitationPage = lazy(() => import('./pages/PublicInvitationPage'));
const MyInvitationsPage = lazy(() => import('./pages/MyInvitationsPage'));
const InvitationDetailPage = lazy(() => import('./pages/InvitationDetailPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const CommentsPage = lazy(() => import('./pages/CommentsPage'));

const PrisonLandingPage = lazy(() => import('./pages/PrisonLandingPage'));

// Loading component
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh', 
    background: '#121212',
    color: '#2DB47F'
  }}>
    <div className="loader">Yükleniyor...</div>
  </div>
);

function UTMTracker() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
    
    let hasMarketingData = false;
    const utmData: any = {};

    utmFields.forEach(field => {
      const value = params.get(field);
      if (value) {
        utmData[field] = value;
        hasMarketingData = true;
      }
    });

    if (params.get('gad_source') === '1' && !utmData.utm_source) {
      utmData.utm_source = 'google';
      utmData.utm_medium = 'cpc';
      hasMarketingData = true;
    }

    if (params.get('gad_campaignid') && !utmData.utm_campaign) {
      utmData.utm_campaign = params.get('gad_campaignid');
      hasMarketingData = true;
    }

    if (hasMarketingData) {
      utmData.referrer_url = window.location.href;
      utmData.captured_at = new Date().toISOString();
      localStorage.setItem('postacim_utm_data', JSON.stringify(utmData));
    }
  }, []);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <PrisonProvider>
        <Toaster position="top-center" containerStyle={{ top: 'max(40px, env(safe-area-inset-top))' }} toastOptions={{
          style: {
            background: '#181818',
            color: '#fff',
            border: '1px solid #333',
            marginTop: '10px'
          }
        }} />
        <Router>
          <UTMTracker />
          <AppChoiceModal />
          <ScrollToTop />
          <MetaPixelTracker />
          <div className="App">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <Layout>
                    <HomePage />
                  </Layout>
                } />
                <Route path="/giris-yap" element={<LoginPage />} />
                <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
                <Route path="/kayit-ol" element={<RegisterPage />} />
                <Route path="/login" element={<Navigate to="/giris-yap" replace />} />
                <Route path="/forgot-password" element={<Navigate to="/sifremi-unuttum" replace />} />
                <Route path="/register" element={<Navigate to="/kayit-ol" replace />} />
                <Route path="/r/:code" element={<ReferralHandler />} />
                <Route path="/i/:slug" element={<PublicInvitationPage />} />
                <Route path="/blog" element={
                  <Layout>
                    <BlogListPage />
                  </Layout>
                } />
                <Route path="/blog/:slug" element={
                  <Layout>
                    <BlogPostPage />
                  </Layout>
                } />

                {/* Public Protected Routes */}
                <Route path="/hizmetler" element={
                  <Layout>
                    <LetterCategoryPage stageOverride="initial" />
                  </Layout>
                } />

                <Route path="/hizmetler/mektup" element={
                  <Layout>
                    <LetterCategoryPage stageOverride="mektup" />
                  </Layout>
                } />

                <Route path="/cezaevine-mektup" element={
                  <Layout>
                    <PrisonLandingPage />
                  </Layout>
                } />

                <Route path="/cezaevine-mektup/yaz" element={
                  <Layout>
                    <NewLetterPage categoryOverride="prison" />
                  </Layout>
                } />

                <Route path="/askere-mektup" element={
                  <Layout>
                    <NewLetterPage categoryOverride="military" />
                  </Layout>
                } />

                <Route path="/sevgiliye-mektup" element={
                  <Layout>
                    <NewLetterPage categoryOverride="lover" />
                  </Layout>
                } />

                <Route path="/gelecege-mektup" element={
                  <Layout>
                    <NewLetterPage categoryOverride="future" />
                  </Layout>
                } />

                <Route path="/normal-mektup" element={
                  <Layout>
                    <NewLetterPage categoryOverride="normal" />
                  </Layout>
                } />

                <Route path="/hizmetler/mektup-gonder/:category" element={
                  <Layout>
                    <NewLetterPage />
                  </Layout>
                } />

                {/* Protected Routes */}
                <Route path="/hesabim" element={
                  <ProtectedRoute>
                    <Layout>
                      <AccountPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/account" element={<Navigate to="/hesabim" replace />} />

                <Route path="/cuzdanim" element={
                  <ProtectedRoute>
                    <Layout>
                      <WalletPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/wallet" element={<Navigate to="/cuzdanim" replace />} />

                <Route path="/taslaklar" element={
                  <ProtectedRoute>
                    <Layout>
                      <DraftLettersPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/draft-letters" element={<Navigate to="/taslaklar" replace />} />

                <Route path="/gonderilen-mektuplar" element={
                  <ProtectedRoute>
                    <Layout>
                      <SentLettersPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/sent-letters" element={<Navigate to="/gonderilen-mektuplar" replace />} />

                <Route path="/davetiyelerim" element={
                  <ProtectedRoute>
                    <Layout>
                      <MyInvitationsPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/davetiye-olustur" element={
                  <ProtectedRoute>
                    <Layout>
                      <InvitationEditorPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/davetiye-duzenle/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <InvitationEditorPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/davetiye/:id/detay" element={
                  <ProtectedRoute>
                    <Layout>
                      <InvitationDetailPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/mektup/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <LetterDetailPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Public Info Pages */}
                <Route path="/gizlilik-politikasi" element={
                  <Layout>
                    <PrivacyPolicyPage />
                  </Layout>
                } />
                <Route path="/privacy-policy" element={<Navigate to="/gizlilik-politikasi" replace />} />

                <Route path="/kullanim-kosullari" element={
                  <Layout>
                    <TermsOfServicePage />
                  </Layout>
                } />
                <Route path="/terms-of-service" element={<Navigate to="/kullanim-kosullari" replace />} />

                <Route path="/kvkk-aydinlatma" element={
                  <Layout>
                    <KvkkAydinlatmaPage />
                  </Layout>
                } />

                <Route path="/mesafeli-satis-sozlesmesi" element={
                  <Layout>
                    <DistanceSellingAgreementPage />
                  </Layout>
                } />

                <Route path="/iletisim" element={
                  <Layout>
                    <ContactPage />
                  </Layout>
                } />
                <Route path="/contact" element={<Navigate to="/iletisim" replace />} />

                <Route path="/takip/:barcode" element={
                  <Layout>
                    <TrackingPage />
                  </Layout>
                } />

                <Route path="/fiyatlandirma" element={
                  <Layout>
                    <PricingPage />
                  </Layout>
                } />
                
                <Route path="/sikca-sorulan-sorular" element={
                  <Layout>
                    <FAQPage />
                  </Layout>
                } />

                <Route path="/kullanici-yorumlari" element={
                  <Layout>
                    <CommentsPage />
                  </Layout>
                } />
                <Route path="/comments" element={<Navigate to="/kullanici-yorumlari" replace />} />

                <Route path="/abonelikten-ayril" element={
                  <UnsubscribePage />
                } />
                <Route path="/unsubscribe" element={<Navigate to="/abonelikten-ayril" replace />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </PrisonProvider>
    </AuthProvider>
  );
}

export default App;